import React from 'react';
import {Spinner} from 'react-bootstrap';
import PaymentMethodItem from './PaymentMethodItem';
import './PaymentMethodList.css';

/**
 * PaymentMethodList Component
 *
 * Displays a list of payment methods with loading and empty states
 *
 * @param {Object} props
 * @param {Array} props.paymentMethods - Array of payment method objects
 * @param {string} props.selectedMethod - Currently selected payment method BIC
 * @param {Function} props.onSelectMethod - Callback when a payment method is selected
 * @param {boolean} props.isLoading - Whether payment methods are being loaded
 * @param {boolean} props.disabled - Whether all payment methods are disabled
 * @param {string} props.cartType - Type of cart (normal or express) for styling
 * @param {string} props.title - Title for the payment methods section
 * @param {Function} props.t - Translation function
 */
const PaymentMethodList = ({
	                           paymentMethods = [],
	                           selectedMethod,
	                           onSelectMethod,
	                           isLoading = false,
	                           disabled = false,
	                           cartType = 'normal',
	                           title,
	                           t = (key) => key
                           }) => {
	if (isLoading) {
		return (
			<div className="payment-method-list-wrapper">
				{title && (
					<div className={`payment-method-list-header ${cartType}`}>
						{title}
					</div>
				)}
				<div className="payment-method-list-loading">
					<Spinner
						animation="border"
						style={{
							color: `${cartType === "express" ? "#e83e8c" : "#9cc54b"}`,
							width: '50px',
							height: '50px',
							borderWidth: '4px'
						}}
					/>
					<p className="loading-text">
						{t("loading_payment_methods") || "Loading payment methods..."}
					</p>
				</div>
			</div>
		);
	}

	if (!paymentMethods || paymentMethods.length === 0) {
		return (
			<div className="payment-method-list-wrapper">
				{title && (
					<div className={`payment-method-list-header ${cartType}`}>
						{title}
					</div>
				)}
				<div className="payment-method-list-empty">
					<svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path
							d="M20 7H4C2.89543 7 2 7.89543 2 9V19C2 20.1046 2.89543 21 4 21H20C21.1046 21 22 20.1046 22 19V9C22 7.89543 21.1046 7 20 7Z"
							stroke="#d1d1d1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
						<path d="M16 3H8C6.89543 3 6 3.89543 6 5V7H18V5C18 3.89543 17.1046 3 16 3Z" stroke="#d1d1d1" strokeWidth="2"
						      strokeLinecap="round" strokeLinejoin="round"/>
						<path d="M2 12H22" stroke="#d1d1d1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
					</svg>
					<p className="empty-text">
						{t("no_payment_methods_available") || "No payment methods available"}
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="payment-method-list-wrapper">
			{title && (
				<div className={`payment-method-list-header ${cartType}`}>
					{title}
				</div>
			)}
			<div className="payment-method-list" role="radiogroup" aria-label="Payment methods">
				{paymentMethods.map((method, index) => (
					<PaymentMethodItem
						key={method.bic || index}
						method={method}
						isSelected={selectedMethod === method.bic}
						onSelect={onSelectMethod}
						disabled={disabled}
						cartType={cartType}
					/>
				))}
			</div>
		</div>
	);
};

export default PaymentMethodList;
